import React, { memo, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { batch, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { changeEaState } from '@store/actions/eaAction';
import { toast } from 'react-toastify';
import {
  isSuccessStatus,
  getUniqArray,
  isEmptyObject,
  isEmptyString,
  isNil,
  isFunction,
} from '@common/utils';
import SatModal from '@components/common/satUI/SatModal';
import {
  ForeignCurrencySuitabilityTestLnb,
  ForeignCurrencySuitabilityAuthMain,
  ForeignCurrencySuitabilityTestForm,
  SignAgree,
  ForeignCurrencySuitabilityTestResult,
  ForeignCurrencySuitabilityDigitalSignaturePopup,
} from '@container/customer';

/**
 * 외화보험 적합성 진단
 */
const ForeignCurrencySuitabilityTest = ({
  userInfo,
  customerInfo,
  handleCloseModalButtonClick,
  updateCustomerInfo,
  showModal,
  chkCertiSms,
  verifySmsAuthNo,
  retrySmsAuthNo,
  verifyName,
  checkIdentityCard,
  saveSignNameData,
  saveSignatureData,
  sendTestSms,
  postSuitability,
  getSuitabilityKeys,
  postSuitabilityReport,
  postSuitabilityByPlan,
  handleConfirmButtonClick,
  isShowLnb,
  onComplete,
}) => {
  const location = useLocation();

  // const { confirm } = $hooks.useModal();
  const { changeOrientation, isPortrait } = $hooks.useDevice();
  const [signInfoList, setSignInfoList] = useState([
    {
      step: 1,
      reportId: 'remote_app_form_etc_18',
      signIds: {
        name: 'customerSuitNameSign',
        signature: 'customerSuitSign',
      },
      signName: '',
      signature: '',
    },
    {
      step: 2,
      reportId: 'remote_app_form_etc_32',
      signIds: {
        name: 'customerSuitNameSign',
        signature: 'customerSuitSign',
      },
      signName: '',
      signature: '',
    },
    {
      step: 3,
      reportId: 'remote_app_form_etc_32',
      signIds: {
        name: 'customerResultNameSign',
        signature: 'customerResultSign',
      },
      signName: '',
      signature: '',
    },
  ]);
  const [currentStep, setCurrentStep] = useState({ main: 1, sub: 1 });
  const [completedStepList, setCompletedStepList] = useState([]);
  const [isShowSignPopup, setShowSignPopup] = useState(false); // 서명팝업 노출 여부
  const [isAuthCompleted, setIsAuthCompleted] = useState(false); // 본인인증 완료 여부
  const [suitabilityFormData, setSuitabilityFormData] = useState({});
  const [suitabilityKeys, setSuitabilityKeys] = useState({
    applicationNo: '', // 발행번호
    reportSerialNo: '', // 진단지 번호
  });
  const [certiHistoryNo, setCertiHistoryNo] = useState();
  const [prevAnswer, setPrevAnswer] = useState({});
  const [prevEtcText, setPrevEtcText] = useState('');
  const [notSuitableReasons, setNotSuitableReasons] = useState([]);
  const dispatch = useDispatch();

  const getSignData = useCallback((currentStep, defaultSignInfo) => {
    if (1 === currentStep.main) {
      return {
        signName: '',
        signature: '',
      };
    }
    return {
      signName: defaultSignInfo.signName,
      signature: defaultSignInfo.signature,
    };
  }, []);

  const mutateSignInfoList = useCallback(
    (targetStep, nextSignInfo) =>
      setSignInfoList(prev =>
        prev.filter(l => targetStep !== l.step).concat(nextSignInfo),
      ),
    [],
  );

  const setResultStatus = useCallback(
    acc => status => {
      !isNil(status) && acc.push(status);
      return acc;
    },
    [],
  );

  const getCurrentStepSignInfo = useCallback(
    (signInfoList, currentStep, signName, signature) =>
      Object.assign(
        {},
        signInfoList?.find(l => currentStep.main === l.step) ?? {},
        { signName, signature },
      ),
    [],
  );

  /**
   * 서명동의 - 이름
   */
  const postSignName = useCallback((signInfo, applicationNo, handleSuccess) => {
    const signNameCallback =
      signInfo =>
      ({ status, data, payload }) => {
        if (isSuccessStatus(status) && '0' === data?.tranCode) {
          isFunction(handleSuccess) && handleSuccess(status);
          mutateSignInfoList(
            signInfo.step,
            Object.assign(signInfo, {
              signName: payload.signData,
            }),
          );
        }
      };

    saveSignNameData({
      signGubun: 'P',
      signData: signInfo.signName,
      reportId: signInfo.reportId,
      signId: signInfo.signIds.name,
      applicationNo,
      callback: signNameCallback(signInfo),
    });
  }, []);

  /**
   * 서명동의 - 서명
   */
  const postSignature = useCallback(
    (signInfo, applicationNo, handleSuccess) => {
      const signatureCallback =
        signInfo =>
        ({ status, data, payload }) => {
          if (isSuccessStatus(status) && '0' === data?.tranCode) {
            isFunction(handleSuccess) && handleSuccess(status);
            mutateSignInfoList(
              signInfo.step,
              Object.assign(signInfo, {
                signature: payload.signData,
              }),
            );
          }
        };

      saveSignatureData({
        signGubun: 'P',
        applicationNo,
        signData: signInfo.signature,
        reportId: signInfo.reportId,
        signId: signInfo.signIds.signature,
        callback: signatureCallback(signInfo),
      });
    },
    [],
  );

  /**
   * 적합성 진단지 저장
   */
  const submitSuitabilityTest = useCallback(
    (
      pathname,
      suitabilityFormData,
      suitabilityKeys,
      postSuitabilityCallback,
    ) => {
      // 고객정보 화면
      if (
        ['/home/customerInfo']
          .map(p => p.toLowerCase())
          .includes((pathname ?? '').toLowerCase())
      ) {
        postSuitability(
          Object.assign({}, suitabilityFormData, {
            applicationNo: suitabilityKeys.applicationNo,
            isUsd: true, // 외화적합성 여부
            callback: postSuitabilityCallback,
          }),
        );
      } else {
        // 가입설계 화면
        postSuitabilityByPlan(
          Object.assign({}, suitabilityFormData, {
            applicationNo: suitabilityKeys.applicationNo,
            isUsd: true, // 외화적합성 여부
            callback: postSuitabilityCallback,
          }),
        );
      }
    },
    [],
  );

  /**
   * 적합성 진단지(pdf) 생성 요청
   */
  const submitSuitabilityReport = useCallback(formData => {
    const getValueArr = answs => {
      /**
       * 답변 object 생성
       */
      // const getAnswer = (num, val) => {
      //   // 0번문항 제외, 선택된 번호 - 1 적용 (ex)1번 선택 시, 0번
      //   return 0 === num
      //     ? val
      //     : val
      //         .split('|')
      //         .map(a => Number(a))
      //         .join('|');
      // };
      return (
        answs?.split(',')?.reduce((acc, v, i) => {
          acc[`q_${i}`] = v;
          // acc[`q_${i}`] = getAnswer(i, v);
          return acc;
        }, {}) ?? {}
      );
    };

    /**
     * 선택된 번호 수량 조회 (단일선택:1, 다중선택: 선택된 수량)
     */
    const getAnswerCnt = answs => {
      return answs
        .split(',')
        .reduce((acc, v, i) => {
          if (i === 10) {
            // 10 번 문항에 6번 기타 선택시에는 텍스트 입력 카운트 -1
            return String(v).split('|').includes('6')
              ? `${acc}${String(v).split('|').length - 1},`
              : `${acc}${String(v).split('|').length},`;
          }
          return `${acc}${String(v).split('|').length},`;
        }, '')
        .trim()
        .slice(0, -1);
    };

    /**
     * 진단지 생성 요청 객체 생성
     */
    const getSuitabilityReportDto = formData => ({
      age: formData.legalAge,
      score: formData.score,
      birthDate: formData.birthDate,
      customerId: formData.customerId,
      invTypeCode: formData.invTypeCode,
      prdTypeCode: formData.prdTypeCode,
      invTypeName: formData.invTypeText,
      prdTypeName: formData.prdTypeText,
      email: formData.emailAddr,
      customerName: formData.name,
      cpNo: formData.mobileTelNo,
      certiHistoryNo: formData.certiHistoryNo,
      serialNo: formData.reportSerialNo,
      publishNumber: formData.applicationNo,
      valueArr: getValueArr(formData.answs ?? ''),
      answerCnt: getAnswerCnt(formData.answs ?? ''),
      stbltSno: formData.stbltSno,
      isUsd: 1,
    });

    const requestDto = getSuitabilityReportDto(formData);
    const reportCallback = ({ status, data, payload }) => {
      // [Robin]: 테스트 URL console.log로 대체
      console.log('reportCallback() - PDF URL', data.pdfPath);
      // // 개발계 테스트 url 확인 하기 위해
      // if ($utils.isShowDevFunction) {
      //   confirm({
      //     message: `개발 Test URL: ${data.pdfPath}`,
      //     okLabel: '링크 열기',
      //     cancelLabel: '닫기',
      //     onClose: isYes => {
      //       if (isYes) $utils.openUrl(data.pdfPath);
      //     },
      //   });
      // }

      onComplete(true);
    };
    const suitabilityReportData = { ...requestDto, callback: reportCallback };

    // 진단지 생성 요청
    postSuitabilityReport(suitabilityReportData);
  }, []);

  /**
   * 성명, 서명 저장
   */
  const submitSigns = useCallback(
    (signInfoList, currentStep, signName, signature, suitabilityKeys) => {
      const currentStepSignInfo = getCurrentStepSignInfo(
        signInfoList,
        currentStep,
        signName,
        signature,
      );

      //진단지, 서명 저장
      if (!isEmptyString(suitabilityKeys.applicationNo)) {
        const resultList = [];

        batch(() => {
          postSignName(
            currentStepSignInfo,
            suitabilityKeys.applicationNo,
            setResultStatus(resultList),
          );
          postSignature(
            currentStepSignInfo,
            suitabilityKeys.applicationNo,
            setResultStatus(resultList),
          );
        });
      }
    },
    [],
  );

  /**
   * 전자서명 동의 버튼 클릭
   */
  const handleSignButtonClick = useCallback(
    (
        customerInfo,
        currentStep,
        completedStepList,
        signInfoList,
        suitabilityFormData,
        suitabilityKeys,
      ) =>
      (signName, signature) => {
        const currentStepSignInfo = Object.assign(
          {},
          signInfoList.find(l => currentStep.main === l.step),
          { signName, signature },
        );

        if (1 === currentStep.main) {
          if (
            [
              suitabilityKeys.applicationNo,
              suitabilityKeys.reportSerialNo,
            ].every(n => !isEmptyString(n))
          ) {
            const resultList = []; // 서명 저장 결과목록
            batch(() => {
              postSignName(
                currentStepSignInfo,
                suitabilityKeys.applicationNo,
                resultList,
              );
              postSignature(
                currentStepSignInfo,
                suitabilityKeys.applicationNo,
                resultList,
              );
            });
            completedStepList.some(l => 1 === l.main && 1 === l.sub)
              ? setCurrentStep({ main: 2, sub: 1 })
              : setCurrentStep({ main: 1, sub: 1 });

            setCompletedStepList(prev =>
              getUniqArray(prev.concat({ main: 1, sub: 2 })),
            );
          } else {
            console.error('something wrong in workflow.');
          }
        } else if (2 === currentStep.main) {
          if (
            !isEmptyObject(suitabilityFormData) &&
            suitabilityKeys.applicationNo
          ) {
            const resultList = []; // 서명 저장 결과목록

            // 2단계 서명저장
            batch(() => {
              postSignName(
                currentStepSignInfo,
                suitabilityKeys.applicationNo,
                setResultStatus(resultList),
              );
              postSignature(
                currentStepSignInfo,
                suitabilityKeys.applicationNo,
                setResultStatus(resultList),
              );
            });

            setCurrentStep({ main: 3, sub: 1 });
            setCompletedStepList(prev =>
              getUniqArray(prev.concat({ main: 2, sub: 1 })),
            );
          }
        } else if (3 === currentStep.main) {
          // 3단계 서명 저장
          if (!isEmptyString(suitabilityKeys.applicationNo)) {
            const submitSuitabilityTestCallback =
              (
                suitabilityFormData,
                signInfoList,
                currentStep,
                signName,
                signature,
                suitabilityKeys,
              ) =>
              response => {
                submitSigns(
                  signInfoList,
                  currentStep,
                  signName,
                  signature,
                  suitabilityKeys,
                );

                const data = response?.data || {};

                // 외화 적합성 진단지 출력
                submitSuitabilityReport({
                  ...suitabilityFormData,
                  stbltSno: data.stbltSno,
                });

                setCompletedStepList(prev =>
                  getUniqArray(prev.concat({ main: 3, sub: 1 })),
                );
                handleConfirmButtonClick();
              };

            // 진단지 저장 후 서명정보 저장
            submitSuitabilityTest(
              location.pathname,
              suitabilityFormData,
              suitabilityKeys,
              submitSuitabilityTestCallback(
                suitabilityFormData,
                signInfoList,
                currentStep,
                signName,
                signature,
                suitabilityKeys,
              ),
            );
          }
        }
        setShowSignPopup(() => false);
      },
    [],
  );

  // 세로모드일때 LNB 외부 클릭 시 LNB 닫기
  const handleCloseLnb = useCallback((isPortrait, isShowLnb) => {
    if (isPortrait && isShowLnb) {
      dispatch(
        changeEaState({
          type: 'isShowLnb',
          data: false,
        }),
      );
    }
  }, []);

  useEffect(() => {
    toast('대면적합성진단의 경우 진단일 기준 1회만 가능합니다.');
    const cleanUp = () => {
      setCurrentStep(() => ({ main: 1, sub: 1 }));
      setCompletedStepList(() => []);
      setSuitabilityFormData(() => ({}));
      setSuitabilityKeys({
        applicationNo: '', // 발행번호
        reportSerialNo: '', // 진단지 번호
      });

      changeOrientation('landscape'); // 가로 전환
    };

    return cleanUp;
  }, []);

  return (
    <div className="container">
      <div
        className="dim"
        style={{
          display: isPortrait && isShowLnb ? 'block' : 'none',
        }}
        onClick={() => handleCloseLnb(isPortrait, isShowLnb)}
      />
      <ForeignCurrencySuitabilityTestLnb
        customerInfo={customerInfo}
        handleCloseModalButtonClick={handleCloseModalButtonClick}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        completedStepList={completedStepList}
        setCompletedStepList={setCompletedStepList}
        showModal={showModal}
        isShowLnb={isShowLnb}
      />
      <div
        id="content"
        className="content"
        // className={`content ${isPortrait ? 'bottom-gradient' : ''}`}
      >
        {/* 본인인증 */}
        {1 === currentStep.main && 1 === currentStep.sub && (
          <ForeignCurrencySuitabilityAuthMain
            customerInfo={customerInfo}
            setCurrentStep={setCurrentStep}
            completedStepList={completedStepList}
            setCompletedStepList={setCompletedStepList}
            updateCustomerInfo={updateCustomerInfo}
            showModal={showModal}
            chkCertiSms={chkCertiSms}
            verifySmsAuthNo={verifySmsAuthNo}
            sendTestSms={sendTestSms}
            retrySmsAuthNo={retrySmsAuthNo}
            verifyName={verifyName}
            checkIdentityCard={checkIdentityCard}
            getSuitabilityKeys={getSuitabilityKeys}
            setSuitabilityKeys={setSuitabilityKeys}
            suitabilityKeys={suitabilityKeys}
            setCertiHistoryNo={setCertiHistoryNo}
            agreeButtonColor={'yellow'}
            isUsd={true}
            onComplete={() => setIsAuthCompleted(true)}
          />
        )}
        {/* 전자문서 서명동의 */}
        {1 === currentStep.main && 2 === currentStep.sub && (
          <SignAgree
            currentStep={currentStep}
            setShowSignPopup={setShowSignPopup}
            agreeButtonColor={'yellow'}
            isAuthCompleted={isAuthCompleted}
          />
        )}
        {/* 외화보험 적합성 진단 */}
        {2 === currentStep.main && 1 === currentStep.sub && (
          <ForeignCurrencySuitabilityTestForm
            userInfo={userInfo}
            customerInfo={customerInfo}
            setCurrentStep={setCurrentStep}
            setCompletedStepList={setCompletedStepList}
            showModal={showModal}
            pageConfirmClick={setShowSignPopup}
            setSuitabilityFormData={setSuitabilityFormData}
            suitabilityKeys={suitabilityKeys}
            certiHistoryNo={certiHistoryNo}
            prevAnswer={prevAnswer}
            setPrevAnswer={setPrevAnswer}
            prevEtcText={prevEtcText}
            setPrevEtcText={setPrevEtcText}
            setNotSuitableReasons={setNotSuitableReasons}
          />
        )}
        {/* 외화보험 적합성 진단 결과 확인 */}
        {3 === currentStep.main && 1 === currentStep.sub && (
          <ForeignCurrencySuitabilityTestResult
            customerInfo={customerInfo}
            suitabilityFormData={suitabilityFormData}
            handleNotSuitableTest={() => {
              // 부적합시, 사인정보 저장 없이 진단지만 저장
              submitSuitabilityTest(
                location.pathname,
                suitabilityFormData,
                suitabilityKeys,
                response => {
                  const data = response?.data || {};

                  // 적합성 진단지 출력
                  submitSuitabilityReport({
                    ...suitabilityFormData,
                    stbltSno: data.stbltSno,
                  });
                  handleConfirmButtonClick();
                },
              );
            }}
            pageConfirmClick={setShowSignPopup}
            answers={suitabilityFormData.answs}
            notSuitableReasons={notSuitableReasons}
          />
        )}
      </div>

      {/* 전자서명 모달 */}
      <SatModal
        visible={isShowSignPopup}
        type="info"
        wide
        setVisible={() => setShowSignPopup(false)}
      >
        <ForeignCurrencySuitabilityDigitalSignaturePopup
          currentStep={currentStep}
          signData={getSignData(
            currentStep,
            signInfoList?.find(info => info.step === 1) ?? {
              signName: '',
              signature: '',
            },
          )}
          customerName={customerInfo.name}
          handleSignButtonClick={handleSignButtonClick(
            customerInfo,
            currentStep,
            completedStepList,
            signInfoList,
            suitabilityFormData,
            suitabilityKeys,
          )}
        />
      </SatModal>
    </div>
  );
};

ForeignCurrencySuitabilityTest.propTypes = {
  userInfo: PropTypes.object.isRequired,
  customerInfo: PropTypes.object.isRequired,
  handleCloseModalButtonClick: PropTypes.func.isRequired,
  updateCustomerInfo: PropTypes.func.isRequired,
  showModal: PropTypes.func.isRequired,
  chkCertiSms: PropTypes.func.isRequired,
  verifySmsAuthNo: PropTypes.func.isRequired,
  retrySmsAuthNo: PropTypes.func.isRequired,
  verifyName: PropTypes.func.isRequired,
  checkIdentityCard: PropTypes.func.isRequired,
  saveSignNameData: PropTypes.func.isRequired,
  saveSignatureData: PropTypes.func.isRequired,
  sendTestSms: PropTypes.func.isRequired,
  postSuitability: PropTypes.func,
  getSuitabilityKeys: PropTypes.func.isRequired,
  postSuitabilityReport: PropTypes.func.isRequired,
  postSuitabilityByPlan: PropTypes.func,
  handleConfirmButtonClick: PropTypes.func,
  isPortrait: PropTypes.bool,
  isShowLnb: PropTypes.bool,
};

ForeignCurrencySuitabilityTest.defaultProps = {
  isPortrait: false,
  isShowLnb: false,
  onComplete: () => {},
};

export default memo(ForeignCurrencySuitabilityTest);
